import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

/* /employers
   SEO title: Hire Employees in India – Post Jobs Online | PlanB Jobs */

const JOB_FIELDS = [
  'Job title', 'Job description', 'Required skills', 'Experience', 'Salary range',
  'Location', 'Employment type', 'Education', 'Application details',
];
const DASH_ITEMS = [
  'Company profile', 'Job postings', 'Active and expired vacancies',
  'Applications', 'Shortlisted candidates', 'Recruitment activity',
];
const PIPELINE = [
  { stage: 'Applied', people: ['Neha K.', 'Rohit S.', 'Isha M.'] },
  { stage: 'Shortlisted', people: ['Arjun P.', 'Sana R.'] },
  { stage: 'Interview', people: ['Kabir D.'] },
];
const AI_ITEMS = [
  'AI candidate-job matching', 'Candidate relevance scores',
  'Skill-based candidate discovery', 'Resume analysis', 'Candidate recommendations',
];
const SCORES = [
  { name: 'Neha K.', score: 91 },
  { name: 'Arjun P.', score: 84 },
  { name: 'Isha M.', score: 76 },
];
const SEGMENTS = [
  { name: 'Small Businesses', level: 1, text: 'Find employees without expensive recruitment contracts.' },
  { name: 'Startups', level: 2, text: 'Build your team as your business grows.' },
  { name: 'Growing Companies', level: 3, text: 'Manage multiple vacancies efficiently.' },
  { name: 'Enterprises', level: 4, text: 'Explore bulk recruitment and customised solutions.' },
];
const HERO_JOBS = [
  { role: 'Sales Executive', meta: 'Pune · Full-time', live: true },
  { role: 'Frontend Developer', meta: 'Remote · Full-time', live: true },
  { role: 'Office Assistant', meta: 'Mumbai · Part-time', live: false },
];
const HERO_STATS = [
  { n: 6, l: 'Active jobs' },
  { n: 148, l: 'Applications' },
  { n: 12, l: 'Shortlisted' },
];

const AI_HIGHLIGHTS = [
  'Skill-based job matching',
  'Relevant job recommendations',
  'Candidate profile intelligence',
  'Smarter recruitment discovery',
];
const JOURNEY = [
  { n: '01', title: 'Register your company', body: 'Create your employer account and set up your company profile.' },
  { n: '02', title: 'Post your vacancy', body: 'Publish a job with the details candidates need to decide.' },
  { n: '03', title: 'Review and hire', body: 'Manage applicants and move the best candidates forward.' },
];

function useSeo(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    const prev = meta.getAttribute('content');
    meta.setAttribute('content', description);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove(); else if (prev !== null) meta.setAttribute('content', prev);
    };
  }, [title, description]);
}

/* ---------------------------- animation helpers ---------------------------- */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setOn(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setOn(true); io.disconnect(); } },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, on];
}

function Reveal({ children, delay = 0, className = '', variant = 'up' }) {
  const [ref, on] = useInView(0.12);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${variant !== 'up' ? `reveal-${variant}` : ''} ${on ? 'reveal-in' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setP(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="fixed top-0 left-0 h-[3px] z-[100] brand-gradient" style={{ width: `${p}%` }} aria-hidden="true" />;
}

function Check({ className = 'w-3 h-3' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

/* number that counts up once it is visible */
function CountUp({ to, duration = 1400, delay = 0 }) {
  const [ref, on] = useInView(0.3);
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setV(to); return undefined; }
    let raf;
    let start;
    const t = setTimeout(() => {
      const tick = (now) => {
        if (start == null) start = now;
        const p = Math.min((now - start) / duration, 1);
        setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); };
  }, [on, to, duration, delay]);
  return <span ref={ref} className="tabular-nums">{v}</span>;
}

/* bar that fills when it scrolls into view */
function Bar({ label, value, delay = 0 }) {
  const [ref, on] = useInView(0.3);
  return (
    <div ref={ref}>
      <div className="flex justify-between text-sm mb-1.5"><span>{label}</span><b>{value}%</b></div>
      <div className="h-2 rounded-full bg-white/15 overflow-hidden">
        <div
          className="h-full rounded-full bg-[#C7F464] transition-[width] duration-[1400ms] ease-out"
          style={{ width: on ? `${value}%` : '0%', transitionDelay: `${delay}ms` }}
        />
      </div>
    </div>
  );
}

/* list of items that slide in one after another */
function StaggerList({ items, dark = false, className = '' }) {
  const [ref, on] = useInView(0.2);
  return (
    <ul ref={ref} className={className}>
      {items.map((f, i) => (
        <li
          key={f}
          style={{ transitionDelay: `${i * 100}ms` }}
          className={`flex items-center gap-3 text-sm transition-all duration-500 ${on ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
        >
          <span
            style={{ transitionDelay: `${i * 100 + 200}ms` }}
            className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-transform duration-500 ${dark ? 'bg-[#C7F464] text-[#17142A]' : 'brand-gradient text-white'} ${on ? 'scale-100' : 'scale-0'}`}
          >
            <Check className="w-2.5 h-2.5" />
          </span>
          {f}
        </li>
      ))}
    </ul>
  );
}

/* segment bars that grow when visible */
function GrowthBars({ level }) {
  const [ref, on] = useInView(0.4);
  return (
    <div ref={ref} className="flex items-end gap-1.5 h-11" aria-hidden="true">
      {[1, 2, 3, 4].map((n) => (
        <i
          key={n}
          className={`w-3.5 rounded transition-all duration-700 ease-out ${n <= level ? 'brand-gradient' : 'bg-purple-100'}`}
          style={{ height: on ? 10 + n * 8 : 4, transitionDelay: `${n * 120}ms` }}
        />
      ))}
    </div>
  );
}

/* ---------------------------------- page ---------------------------------- */
function Employers() {
  const [user] = useState(() => {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
  });
  const registerLink = '/employer/register';

  useSeo(
    'Hire Employees in India – Post Jobs Online | PlanB Jobs',
    'Hire qualified candidates through PlanB Jobs. Post vacancies, manage applicants and discover recruitment solutions for startups, SMEs and large companies.'
  );

  return (
    <div className="employers-root min-h-screen bg-white overflow-x-clip text-[#1E0F3D]">
      <style>{`
        .brand-gradient { background-image: linear-gradient(90deg,#7C3AED,#DB2777); }
        .employers-root h2 { display:inline-block; background-image:linear-gradient(90deg,#7C3AED,#DB2777); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; color:transparent; }
        .employers-root .brand-gradient h2, .employers-root .animated-gradient h2 { background-image:none; -webkit-text-fill-color:#fff; color:#fff; }
        .animated-gradient { background:linear-gradient(90deg,#7C3AED,#DB2777,#7C3AED); background-size:200% 100%; animation:gradient-shift 8s ease infinite; }
        .marquee-mask { -webkit-mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent); mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent); }

        .reveal { opacity:0; transform:translateY(32px); transition:opacity .7s cubic-bezier(.2,.8,.2,1), transform .7s cubic-bezier(.2,.8,.2,1); }
        .reveal-left { transform:translateX(-56px); }
        .reveal-right { transform:translateX(56px); }
        .reveal-zoom { transform:scale(.92); }
        .reveal-in { opacity:1; transform:none; }

        @keyframes rise { from { opacity:0; transform:translateY(20px);} to { opacity:1; transform:none;} }
        @keyframes float { 0%,100% { transform:translateY(0) scale(1);} 50% { transform:translateY(-24px) scale(1.06);} }
        @keyframes bob { 0%,100% { transform:translateY(0) rotate(2deg);} 50% { transform:translateY(-10px) rotate(-1deg);} }
        @keyframes shimmer { to { background-position:200% center; } }
        @keyframes gradient-shift { 0%,100% { background-position:0% 50%;} 50% { background-position:100% 50%;} }
        @keyframes marquee-ltr { from { transform:translateX(0);} to { transform:translateX(-50%);} }
        @keyframes marquee-rtl { from { transform:translateX(-50%);} to { transform:translateX(0);} }
        @keyframes pulse-ring { 0% { box-shadow:0 0 0 0 rgba(124,58,237,.45);} 100% { box-shadow:0 0 0 16px rgba(124,58,237,0);} }
        @keyframes cta-glow { 0% { box-shadow:0 0 0 0 rgba(255,255,255,.5);} 100% { box-shadow:0 0 0 18px rgba(255,255,255,0);} }

        .rise { animation:rise .8s cubic-bezier(.2,.8,.2,1) both; }
        .float-slow { animation:float 9s ease-in-out infinite; }
        .float-slower { animation:float 13s ease-in-out infinite reverse; }
        .bob { animation:bob 5s ease-in-out infinite; }
        .shimmer-text { background-size:200% auto; animation:shimmer 5s linear infinite; }
        .marquee-ltr { animation:marquee-ltr 40s linear infinite; }
        .marquee-rtl { animation:marquee-rtl 50s linear infinite; }
        .pulse-ring { animation:pulse-ring 2s ease-out infinite; }
        .cta-glow { animation:cta-glow 2.4s ease-out infinite; }

        .shine-btn { position:relative; overflow:hidden; }
        .shine-btn::after { content:''; position:absolute; top:0; left:0; width:30%; height:100%; background:rgba(255,255,255,.3); transform:translateX(-120%) skewX(-20deg); transition:transform .7s ease; pointer-events:none; }
        .shine-btn:hover::after { transform:translateX(420%) skewX(-20deg); }

        @media (prefers-reduced-motion: reduce) {
          .rise,.float-slow,.float-slower,.bob,.shimmer-text,.marquee-ltr,.marquee-rtl,.pulse-ring,.cta-glow,.animated-gradient { animation:none; }
          .reveal { opacity:1; transform:none; transition:none; }
          .shine-btn::after { transition:none; }
        }
      `}</style>

      <ScrollProgress />
      <Navbar user={user} />

      {/* ================================ HERO ================================ */}
      <section className="relative bg-gradient-to-br from-[#FBF5FF] via-white to-[#FDF0F6] pt-14 pb-24 lg:pt-20 lg:pb-28">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#7C3AED]/15 blur-3xl float-slow" />
          <div className="absolute -bottom-40 -left-24 w-[30rem] h-[30rem] rounded-full bg-[#DB2777]/12 blur-3xl float-slower" />
          <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(124,58,237,0.10) 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 items-center">
          <div>
            <div className="rise inline-flex items-center gap-2 mb-5 bg-white border border-purple-100 rounded-full px-4 py-1.5 text-xs font-semibold text-gray-600 shadow-sm">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-[#DB2777] opacity-75 animate-ping motion-reduce:animate-none" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-[#DB2777]" />
              </span>
              For startups, SMEs and large companies
            </div>

            <h1 className="mb-6 text-[40px] sm:text-[56px] font-extrabold leading-[1.05] tracking-tight">
              {['Find the Right People.', 'Build the Right Team.'].map((t, i) => (
                <span
                  key={t}
                  style={{ animationDelay: `${0.08 + i * 0.12}s` }}
                  className="rise shimmer-text block w-fit bg-gradient-to-r from-[#7C3AED] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent"
                >
                  {t}
                </span>
              ))}
            </h1>

            <p className="rise max-w-lg text-lg leading-7 text-gray-600" style={{ animationDelay: '.38s' }}>
              Hiring shouldn't be complicated or expensive. PlanB Jobs helps businesses publish vacancies, connect with candidates and manage recruitment from one platform.
            </p>

            <div className="rise flex flex-wrap gap-3 mt-8" style={{ animationDelay: '.5s' }}>
              <Link to={registerLink} className="shine-btn brand-gradient pulse-ring text-white px-8 py-3.5 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)] transition hover:-translate-y-0.5">
                Post a Job
              </Link>
              <Link to="/pricing" className="px-8 py-3.5 rounded-xl font-semibold text-sm text-[#7C3AED] bg-white border border-[#7C3AED]/40 hover:bg-[#F5F0FA] hover:-translate-y-0.5 transition">
                Pricing
              </Link>
            </div>
          </div>

          {/* dashboard mock */}
          <div className="relative max-w-md w-full rise lg:justify-self-end" style={{ animationDelay: '.25s' }} aria-hidden="true">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#7C3AED]/25 to-[#DB2777]/20 blur-3xl" />

            <div className="relative bg-white rounded-3xl border border-purple-100 shadow-[0_40px_80px_-30px_rgba(124,58,237,0.45)] p-5">
              <div className="flex items-center justify-between">
                <p className="font-bold">Employer dashboard</p>
                <span className="text-xs font-medium text-[#7C3AED] bg-[#F5F0FA] rounded-full px-3 py-1">Sample view</span>
              </div>

              <div className="grid grid-cols-3 gap-2.5 mt-4">
                {HERO_STATS.map((s, i) => (
                  <div key={s.l} className="rounded-xl bg-[#FBF5FF] border border-purple-100 p-3">
                    <p className="text-xl font-extrabold"><CountUp to={s.n} delay={500 + i * 150} /></p>
                    <p className="text-xs text-gray-500">{s.l}</p>
                  </div>
                ))}
              </div>

              <div className="mt-3 divide-y divide-purple-50">
                {HERO_JOBS.map((j, i) => (
                  <div key={j.role} style={{ animationDelay: `${0.9 + i * 0.18}s` }} className="rise flex items-center justify-between gap-3 py-3">
                    <div>
                      <p className="font-semibold text-sm">{j.role}</p>
                      <p className="text-xs text-gray-500">{j.meta}</p>
                    </div>
                    <span className={`text-xs font-bold rounded-lg px-2.5 py-1 whitespace-nowrap ${j.live ? 'text-white brand-gradient' : 'text-gray-500 bg-gray-100'}`}>
                      {j.live ? 'Active' : 'Expired'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bob absolute -bottom-7 -right-2 w-56 brand-gradient text-white rounded-2xl p-4 shadow-xl">
              <p className="text-xs text-white/80">New application</p>
              <p className="font-bold text-sm">Neha K. · 91% relevance</p>
            </div>

            <div className="float-slow absolute -top-5 -left-4 bg-white border border-purple-100 rounded-2xl px-3.5 py-2 shadow-lg flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#C7F464] text-[#17142A] flex items-center justify-center"><Check /></span>
              <span className="text-xs font-bold">Job published</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ SCROLLING STRIP ============================ */}
      <div className="marquee-mask overflow-hidden py-8 space-y-3">
        <div className="marquee-ltr flex w-max">
          {[...JOB_FIELDS, ...JOB_FIELDS, ...JOB_FIELDS, ...JOB_FIELDS].map((l, i) => (
            <span key={i} className="shrink-0 mr-3 px-5 py-2 rounded-full bg-white border border-purple-100 text-sm font-semibold text-[#7C3AED] shadow-sm">{l}</span>
          ))}
        </div>
        <div className="marquee-rtl flex w-max">
          {[...DASH_ITEMS, ...DASH_ITEMS, ...DASH_ITEMS, ...DASH_ITEMS, ...DASH_ITEMS].map((l, i) => (
            <span key={i} className="shrink-0 mr-3 px-5 py-2 rounded-full bg-[#F5F0FA] text-sm font-semibold text-[#DB2777]">{l}</span>
          ))}
        </div>
      </div>

      {/* ============================== HOW IT WORKS ============================== */}
      <section id="how" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Three steps to your next hire</h2>
            <p className="text-gray-500">Post a vacancy, review applicants and move the best candidates forward.</p>
          </div>
        </Reveal>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="hidden md:block absolute top-9 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] opacity-30" aria-hidden="true" />
          {JOURNEY.map((s, i) => (
            <Reveal key={s.n} delay={i * 150} className="h-full">
              <div className="group relative h-full text-center rounded-3xl p-7 bg-white border border-purple-100 transition-all duration-300 hover:-translate-y-2 hover:border-[#DB2777]/40 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)]">
                <div className="relative mx-auto w-[4.5rem] h-[4.5rem] rounded-2xl brand-gradient text-white flex items-center justify-center text-2xl font-extrabold shadow-lg shadow-[#7C3AED]/30 transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
                  {s.n}
                </div>
                <h3 className="text-lg font-bold mt-5 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================================ FEATURES ================================ */}
      <section id="features" className="scroll-mt-24 bg-gradient-to-br from-[#FBF5FF] to-[#FDF0F6] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Recruitment from one platform</h2>
            <p className="text-gray-500 max-w-xl mb-12">Post a vacancy, review applicants and move the best candidates forward without switching tools.</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
            {/* Job posting */}
            <Reveal variant="left" className="md:col-span-4 h-full">
              <article className="h-full rounded-3xl p-7 border border-purple-100 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)]">
                <h3 className="text-xl font-bold mb-2">Easy job posting</h3>
                <p className="text-sm text-gray-500">Publish job openings with everything candidates need to decide.</p>
                <StaggerList items={JOB_FIELDS} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-6" />
              </article>
            </Reveal>

            {/* Verification */}
            <Reveal variant="right" className="md:col-span-2 h-full">
              <article className="h-full rounded-3xl p-7 brand-gradient text-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.6)]">
                <h3 className="text-xl font-bold mb-2">Employer verification</h3>
                <p className="text-sm text-white/85">Build credibility through the employer verification process.</p>
                <div className="inline-flex items-center gap-2.5 mt-6 bg-white/15 border border-white/30 rounded-xl px-3.5 py-2.5 pulse-ring">
                  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" className="float-slow">
                    <path d="M12 2l8 3v6c0 5-3.4 9.4-8 11-4.6-1.6-8-6-8-11V5l8-3z" fill="#fff" />
                    <path d="M8 12l3 3 5-6" fill="none" stroke="#7C3AED" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-sm font-semibold">Verified employer</span>
                </div>
                <p className="text-xs text-white/70 mt-4">The badge appears only after successful verification.</p>
              </article>
            </Reveal>

            {/* Dashboard */}
            <Reveal className="md:col-span-3 h-full">
              <article className="h-full rounded-3xl p-7 bg-[#17142A] text-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(23,20,42,0.7)]">
                <h3 className="text-xl font-bold mb-2">Employer dashboard</h3>
                <p className="text-sm text-white/70">Manage your hiring in one place.</p>
                <StaggerList items={DASH_ITEMS} dark className="mt-6 space-y-3.5" />
              </article>
            </Reveal>

            {/* Candidate management */}
            <Reveal delay={120} className="md:col-span-3 h-full">
              <article className="h-full rounded-3xl p-7 border border-purple-100 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)]">
                <h3 className="text-xl font-bold mb-2">Candidate management</h3>
                <p className="text-sm text-gray-500">Organise applicants and manage their recruitment progress.</p>
                <div className="grid grid-cols-3 gap-2.5 mt-6">
                  {PIPELINE.map((c, ci) => (
                    <Reveal key={c.stage} delay={ci * 150} className="h-full">
                      <div className="h-full bg-[#FBF5FF] border border-purple-100 rounded-xl p-2.5 space-y-1.5 content-start">
                        <p className="text-xs font-semibold text-[#7C3AED]">{c.stage}</p>
                        {c.people.map((p) => (
                          <span key={p} className="block text-xs bg-white border border-purple-100 rounded-lg px-2 py-1.5 transition hover:-translate-y-0.5 hover:shadow-sm">{p}</span>
                        ))}
                      </div>
                    </Reveal>
                  ))}
                </div>
              </article>
            </Reveal>

            {/* AI */}
            <Reveal className="md:col-span-6 h-full">
              <article className="relative overflow-hidden h-full rounded-3xl p-7 lg:p-9 bg-[#17142A] text-white grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-all duration-300 hover:-translate-y-1.5">
                <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#7C3AED]/30 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
                <div className="relative">
                  <h3 className="text-xl font-bold mb-2">AI recruitment capabilities</h3>
                  <p className="text-sm text-white/70 max-w-md">Where implemented, offer:</p>
                  <StaggerList items={AI_ITEMS} dark className="mt-4 space-y-2.5" />
                </div>
                <div className="relative">
                  <div className="space-y-3.5">
                    {SCORES.map((r, i) => (
                      <Bar key={r.name} label={r.name} value={r.score} delay={i * 200} />
                    ))}
                  </div>
                  <p className="text-xs text-white/50 mt-4">Sample relevance scores for illustration.</p>
                </div>
              </article>
            </Reveal>

            {/* Pricing */}
            <Reveal variant="zoom" className="md:col-span-6 h-full">
              <article className="animated-gradient relative overflow-hidden rounded-3xl p-7 lg:p-9 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.6)]">
                <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-white/20 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
                <div className="relative">
                  <h3 className="text-xl font-bold mb-2">Affordable recruitment</h3>
                  <p className="text-sm text-white/85">Recruitment solutions for every business size.</p>
                </div>
                <Link to="/pricing" className="relative inline-block bg-white text-[#7C3AED] px-7 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition hover:-translate-y-0.5 hover:shadow-lg">
                  Explore Pricing
                </Link>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================ LESS SEARCHING ============================ */}
<section id="ai-recruitment" className="scroll-mt-24 px-6 lg:px-8 py-10">
  <div className="max-w-7xl mx-auto relative overflow-hidden rounded-[2rem] brand-gradient px-8 lg:px-16 py-14 lg:py-20 text-white shadow-[0_40px_80px_-30px_rgba(124,58,237,0.6)]">
    <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }} aria-hidden="true" />
    <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/20 blur-3xl float-slower pointer-events-none" aria-hidden="true" />
    <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#DB2777]/40 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <div>
        <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] mb-5">Less Searching. More Matching.</h2>
        <p className="text-white/85 text-base lg:text-lg leading-relaxed max-w-xl mb-8">
          Finding a job should be about finding the right fit, not sending hundreds of applications. PlanB Jobs is designed to make job discovery smarter through AI-powered recruitment capabilities.
        </p>
        <Link to="/jobs" className="inline-block bg-white text-[#7C3AED] px-8 py-3.5 rounded-xl font-bold text-sm hover:opacity-90 transition">
          Discover AI Job Matching
        </Link>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {AI_HIGHLIGHTS.map((h, i) => (
          <li key={h} className="list-none">
            <Reveal delay={i * 80} className="h-full">
              <div className="flex items-center gap-3 bg-white/15 border border-white/20 rounded-2xl px-5 py-4 text-sm font-semibold transition duration-300 hover:bg-white/25 hover:-translate-y-1 hover:scale-[1.02]">
                <span className="w-5 h-5 rounded-full bg-white/25 p-1 shrink-0 flex items-center justify-center"><Check className="w-3 h-3" /></span>{h}
              </div>
            </Reveal>
          </li>
        ))}
      </ul>
    </div>
  </div>
</section>

      {/* ================================ SEGMENTS ================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-12">Built for every stage of growth</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SEGMENTS.map((s, i) => (
            <Reveal key={s.name} delay={i * 110} className="h-full">
              <article className="h-full rounded-3xl p-7 bg-white border border-purple-100 transition-all duration-300 hover:-translate-y-2 hover:border-[#DB2777]/40 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)]">
                <GrowthBars level={s.level} />
                <h3 className="text-lg font-bold mt-5 mb-2">{s.name}</h3>
                <p className="text-sm text-gray-500">{s.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================================== CTA ================================== */}
      <section className="px-6 lg:px-8 pb-20 lg:pb-24">
        <Reveal variant="zoom">
          <div className="max-w-7xl mx-auto animated-gradient rounded-[2rem] relative overflow-hidden px-8 lg:px-16 py-14 text-center shadow-[0_40px_80px_-30px_rgba(124,58,237,0.6)]">
            <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }} aria-hidden="true" />
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-white/20 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-white/15 blur-3xl float-slower pointer-events-none" aria-hidden="true" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Start hiring on PlanB Jobs</h2>
              <p className="text-white/85 mb-8">Create your employer account and publish your first vacancy.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to={registerLink} className="cta-glow inline-block bg-white text-[#7C3AED] px-9 py-4 rounded-xl font-bold transition hover:-translate-y-1 hover:shadow-xl">
                  Post a Job
                </Link>
                <Link to="/pricing" className="inline-block text-white px-9 py-4 rounded-xl font-bold border border-white/50 transition hover:-translate-y-1 hover:bg-white/10">
                  Pricing
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}

export default Employers;